import { useMemo, useState } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT, TOGGLEABLE_COLUMNS } from 'common/constants/analyst-dashboards';
import JobsColorSelector from 'dashboard409a/components/JobsColorSelector/JobsColorSelector';
import { EnhancedTableHeadColumn } from '../ValuationsTable.types';

const ValuationsTableConfigToggler = ({ 
  columns, 
  columnVisibility, 
  onColumnsToggle,
  onPaletteClose
}: { 
  columns: EnhancedTableHeadColumn[], 
  columnVisibility: Record<string, boolean>,
  onColumnsToggle: (columnId: string) => void,
  onPaletteClose?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredColumns = useMemo(
    () => columns.filter((column: EnhancedTableHeadColumn) => TOGGLEABLE_COLUMNS.includes(column.id)),
    [columns]
  );
  const financialColumns = useMemo(
    () => filteredColumns.filter(column => FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(column.id)),
    [filteredColumns]
  );
  const otherColumns = useMemo(
    () => filteredColumns.filter(column => !FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(column.id)),
    [filteredColumns]
  );
  
  const areAllFinancialColumnsVisible = useMemo(
    () => financialColumns.every(column => columnVisibility[column.id] !== false),
    [financialColumns, columnVisibility]
  );
  const isAnyFinancialColumnVisible = useMemo(
    () => financialColumns.some(column => columnVisibility[column.id] !== false),
    [financialColumns, columnVisibility]
  );
  
  const handleFinancialsToggle = () => {
    const newState = !areAllFinancialColumnsVisible;
    financialColumns.forEach(column => {
      if (columnVisibility[column.id] !== newState) {
        onColumnsToggle(column.id);
      }
    });
  };
  
  const handleIndividualFinancialToggle = (columnId: string) => {
    onColumnsToggle(columnId);
  };
  
  return (
    <>
      <Tooltip title="Toggle Columns" arrow>
        <IconButton onClick={() => setIsOpen(!isOpen)}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      {isOpen && (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Toggle Columns</DialogTitle>
          <DialogContent>
            <Box sx={{ minWidth: 800, py: 2 }}>
              <Grid container spacing={2}>
                {otherColumns.map((column: EnhancedTableHeadColumn) => (
                  <Grid size={4} key={column.id}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={columnVisibility[column.id] !== false} 
                          onChange={() => onColumnsToggle(column.id)} 
                        />
                      } 
                      label={column.label}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                ))}
              </Grid>
              {financialColumns.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Financials Values
                    </Typography>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={areAllFinancialColumnsVisible} 
                          onChange={handleFinancialsToggle} 
                        />
                      } 
                      label="All"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                  </Box>
                  {isAnyFinancialColumnVisible && (
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      {financialColumns.map((column: EnhancedTableHeadColumn) => (
                        <Grid size={4} key={column.id}>
                          <FormControlLabel 
                            control={
                              <Switch 
                                checked={columnVisibility[column.id] !== false} 
                                onChange={() => handleIndividualFinancialToggle(column.id)} 
                              />
                            } 
                            label={column.label}
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Color Palette
                </Typography>
                <JobsColorSelector onClose={onPaletteClose} />
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ValuationsTableConfigToggler;

